import type { OutreachOperation } from '@discovr/types';
import { zoneAbbrev } from '../../../../lib/timeZones';
import { BODY_MISSING_SKIP, DRAFT_SKIP_LABELS, SUBJECT_MISSING_SKIP, draftSkipCopy } from '../draftLabels';
import { failedTotal } from '../operationOutcomes';
import { skipLabel } from '../outreachSendCopy';
import { errorCopy, hasErrorCopy } from '../sendLabels';
import { shortDate, shortTime, zoneDayKey } from '../shortDate';

const DAY_MS = 86_400_000;

/** "today 18:30" | "tomorrow 00:05" | "30 Sep 00:05", in the campaign zone. */
export function capDeferredWhen(iso: string, zone: string, now: Date): string {
  const at = new Date(iso);
  const time = shortTime(at, zone);
  const today = zoneDayKey(now, zone);
  const tomorrow = zoneDayKey(new Date(new Date(`${today}T12:00:00Z`).getTime() + DAY_MS), 'UTC');
  const key = zoneDayKey(at, zone);
  if (key === today) return `today ${time}`;
  if (key === tomorrow) return `tomorrow ${time}`;
  return `${shortDate(at, zone)} ${time}`;
}

/** Status honesty (owner 2026-10-15): an approval that waits for an email is Scheduled, never Sent. */
export const WAITING_FOR_EMAIL_TITLE = 'Scheduled · waiting for email';

/** UX 1.5: a batch where nothing was applied never reads "Sending started". */
export function bulkResultTitle(input: { applied: number; scheduled: boolean; waiting?: number }): string {
  if (input.applied === 0) return (input.waiting ?? 0) > 0 ? WAITING_FOR_EMAIL_TITLE : 'Nothing sent';
  return input.scheduled ? 'Scheduled' : 'Sending started';
}

/** P32 Q1 (owner): the one sender, shown on Review and the confirmation. Choosing a mailbox is V2. */
export const AUTO_SENDER_LABEL = 'Auto';

/** P29: the owner's wording for the wizard's two send modes. */
export const SEND_NOW_COPY = 'Sends immediately';
export const SCHEDULE_PAST_COPY = 'That time has passed. Pick a later time, or choose Send now.';
export const SCHEDULE_TOO_FAR_COPY = 'Pick a time within 30 days';

/** Z1: Send now runs in the admin's own zone. */
export function sendNowZoneCopy(zone: string): string {
  return `In your time zone: ${zone}`;
}

export function scheduledSendCopy(when: string): string {
  return `Sends at ${when}`;
}

/** The picked time in the campaign zone: "20 Oct, 10:00 IST". */
export function scheduledTimeLabel(scheduledFor: string, zone: string, now: Date = new Date()): string {
  const at = new Date(scheduledFor);
  const year = zoneDayKey(at, zone).slice(0, 4);
  const date = year === zoneDayKey(now, zone).slice(0, 4) ? shortDate(at, zone) : `${shortDate(at, zone)} ${year}`;
  return `${date}, ${shortTime(at, zone)} ${zoneAbbrev(zone, at)}`;
}

/** P32 Q4: creators without a ready draft are dropped from this batch, and the wizard says so. */
export function notInThisSendLine(n: number): string {
  return `${n} ${n === 1 ? 'creator' : 'creators'} not ready — they won't be in this send`;
}

/** Ruling 37: drafting never locks the wizard, and the wizard says so. */
export function draftingLine(done: number, total: number): string {
  return `Drafts are being written (${done} of ${total}). You can close this — they stay saved on each creator's row.`;
}

/** The Drafts step's advisory over the saved template's lint. */
export function templateWarningsLead(n: number): string {
  return `The saved template has ${n} deliverability ${n === 1 ? 'warning' : 'warnings'} — sending isn't blocked.`; // copy: ours
}

/** draft_initial outcome reasons that only drafting produces; the rest are the send vocabulary. */
const DRAFT_OUTCOME_LABELS: Record<string, string> = {
  already_drafted: "A draft is already saved on this creator's row",
  stalled: 'Drafting timed out — write drafts again for this creator',
  not_in_drafts: 'No longer a draft — approved or removed elsewhere',
  subject_missing: SUBJECT_MISSING_SKIP,
  body_missing: BODY_MISSING_SKIP,
};

export function draftOutcomeLabel(reason: string): string {
  return DRAFT_OUTCOME_LABELS[reason] ?? skipLabel(reason);
}

/** P32 Q7: one warning row per daily-cap send time, e.g. "2 scheduled for tomorrow 00:05 (daily cap)". */
export function capDeferredLines(deferredAt: string[], zone: string, now: Date): string[] {
  const byWhen = new Map<string, number>();
  for (const iso of deferredAt) {
    const when = capDeferredWhen(iso, zone, now);
    byWhen.set(when, (byWhen.get(when) ?? 0) + 1);
  }
  return [...byWhen].map(([when, count]) => `${count} scheduled for ${when} (daily cap)`);
}

/** P32 Q6 (owner): "N emails going out now. …" / "N emails scheduled for <time>. …". */
export function confirmationSubtitle(input: { count: number; scheduledAt?: string }): string {
  const emails = `${input.count} ${input.count === 1 ? 'email' : 'emails'}`;
  const what = input.scheduledAt ? `${emails} scheduled for ${input.scheduledAt}` : `${emails} going out now`;
  return `${what}. Replies land in the Inbox.`;
}

/** P32 (Figma 680:64821): creators dropped at Channel and approve's no-address skips, together. */
export function noAddressSkipLine(n: number): string {
  return `${n} ${n === 1 ? 'creator' : 'creators'} skipped — no address on file`;
}

/** The send step's summary: what goes out now, and who waits for an email. */
export function readyToSendLine(n: number): string {
  return `${n} ${n === 1 ? 'email' : 'emails'} ready to send from ${AUTO_SENDER_LABEL}`;
}
export function willWaitLine(n: number): string {
  return `${n} will wait for an email`; // copy: ours
}
/** Owner wording (2026-10-15) for one creator; the plural is the same sentence for a batch. */
export function noEmailWaitMessage(n: number): string {
  return n === 1
    ? 'This creator has no email yet. Once an email is added (by you, or found by our search), it goes out the way you choose here.'
    : `${n} creators have no email yet. Once an email is added (by you, or found by our search), their mail goes out the way you choose here.`; // copy: ours
}
export const NOTHING_CAN_GO_OUT_LINE = 'No email can go out yet.'; // copy: ours

/** A per-creator result status newer than this page: reported, never counted as sent. */
export function unrecognisedResultLine(n: number): string {
  return `${n} ${n === 1 ? 'creator has a result' : 'creators have results'} this page can't show yet — open their row`; // copy: ours
}

/** Approved for a creator with no address yet: nothing is sent until their email arrives. */
export function waitingEmailLine(n: number): string {
  return `${n} will send when their email arrives`; // copy: ours
}

/** The toast when a bulk send finishes: the confirmation's title, then only the counts that are not zero.
 *  A batch that partly failed reads "N of M sending"; `reasons` says why the failed ones did. */
export function sendFinishedText(input: { sent: number; waiting: number; skipped: number; failed: number; scheduled: boolean; total?: number; reasons?: string[] }): string {
  const parts = [
    input.sent > 0 ? `${input.sent} ${input.sent === 1 ? 'email' : 'emails'} ${input.scheduled ? 'scheduled' : 'going out now'}` : null,
    input.waiting > 0 ? waitingEmailLine(input.waiting) : null,
    input.skipped > 0 ? `${input.skipped} skipped` : null,
    input.failed > 0 ? `${input.failed} failed${input.reasons?.length ? `: ${input.reasons.join(' ')}` : ''}` : null,
  ].filter((part): part is string => part !== null);
  const partlyFailed = input.failed > 0 && input.sent > 0;
  const total = Math.max(input.total ?? 0, input.sent + input.waiting + input.skipped + input.failed);
  const title = partlyFailed
    ? `${input.sent} of ${total} ${input.scheduled ? 'scheduled' : 'sending'}`
    : bulkResultTitle({ applied: input.sent, scheduled: input.scheduled, waiting: input.waiting });
  const shown = partlyFailed ? parts.slice(1) : parts;
  return shown.length > 0 ? `${title} — ${shown.join(' · ')}` : title; // copy: ours
}

/** The confirmation's line under "Nothing sent": no email went out, so it never says one is going out. */
export const NOTHING_SENT_SUBTITLE = "No email went out. The drafts stay saved on each creator's row."; // copy: ours

/** U9: suppressed rows never block a batch; they are reported, not failed. */
export function suppressedSkipLine(n: number): string {
  return `${n} can't be sent (do not contact)`;
}

const GO_CODE = /^[A-Z][A-Z0-9_]+$/;
const GO_OPAQUE_REASON = 'internal error';
const UNLISTED_FAILURE_COPY = 'their drafts stay saved; open the creator to try again.';
const UNCOPIED_CODE_COPY = UNLISTED_FAILURE_COPY.charAt(0).toUpperCase() + UNLISTED_FAILURE_COPY.slice(1);

/** Why one approve_drafts item failed. Go writes an error code, a skip-style reason, or its own client-safe sentence. */
export function failureReasonCopy(reason: string | undefined): string {
  const r = reason?.trim() ?? '';
  if (r === '' || r === GO_OPAQUE_REASON) return errorCopy('INTERNAL_ERROR');
  if (GO_CODE.test(r)) return hasErrorCopy(r, 'send') ? errorCopy(r, undefined, 'send') : UNCOPIED_CODE_COPY;
  if (Object.hasOwn(DRAFT_SKIP_LABELS, r)) return draftSkipCopy(r);
  const sentence = r.charAt(0).toUpperCase() + r.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

/** The distinct reasons a send's failed items gave, worded for a send, in the order Go reported them. */
export function failedReasons(operation: OutreachOperation): string[] {
  const copies = (operation.outcomes ?? []).filter((o) => o.outcome === 'failed').map((o) => failureReasonCopy(o.reason));
  return [...new Set(copies)];
}

function namedCreators(ids: number[], names: Record<number, string>): string {
  const known = ids.flatMap((id) => (names[id] ? [`@${names[id]}`] : []));
  if (known.length === 0) return '';
  const rest = ids.length - known.length;
  return `${known.join(', ')}${rest > 0 ? ` and ${rest} more` : ''}`;
}

/** One line per failure reason, naming the creators it hit; failures Go counted but didn't list keep the generic line. */
export function failedLines(operation: OutreachOperation, names: Record<number, string>): string[] {
  const byCopy = new Map<string, number[]>();
  for (const o of operation.outcomes ?? []) {
    if (o.outcome !== 'failed') continue;
    const copy = failureReasonCopy(o.reason);
    byCopy.set(copy, [...(byCopy.get(copy) ?? []), Number(o.itemKey)]);
  }
  const lines = [...byCopy].map(([copy, ids]) => {
    const who = namedCreators(ids, names);
    return `${ids.length} failed — ${who ? `${who}: ` : ''}${copy}`;
  });
  const listed = [...byCopy.values()].reduce((n, ids) => n + ids.length, 0);
  const unlisted = failedTotal(operation) - listed;
  return unlisted > 0 ? [...lines, `${unlisted} failed — ${UNLISTED_FAILURE_COPY}`] : lines;
}

/** o-e2e2 N4: a batch draft regenerated behind the open wizard stays in this send once it is ready; until then, or if
 *  the rewrite failed, Review says why the count is lower than at Continue. */
export function batchChangeLine(rewriting: number, dropped: number): string | null {
  const parts: string[] = [];
  if (rewriting > 0) {
    parts.push(rewriting === 1 ? "1 draft is being rewritten and isn't in this send until it's ready." : `${rewriting} drafts are being rewritten and aren't in this send until they're ready.`);
  }
  if (dropped > 0) {
    parts.push(dropped === 1 ? "1 draft couldn't be rewritten, so that creator isn't in this send." : `${dropped} drafts couldn't be rewritten, so those creators aren't in this send.`);
  }
  return parts.length > 0 ? parts.join(' ') : null;
}
